import axios from "../../axiosInstance";
import requests from '../../requests';

import { takeLatest, all, put, call, delay, select } from "redux-saga/effects";

import { mainActionTypes } from "./main.types";
import {
	fetchDiagramsList,
	fetchDiagramsListSuccess,
	fetchDiagramsListFailure,

	fetchTemplatesListSuccess,
	fetchTemplatesListFailure,

	diagramRenameSuccess,
	diagramRenameFailure,

	diagramCreateSuccess,
	diagramCreateFailure,

	diagramCloneSuccess,
	diagramCloneFailure,

	diagramSoftDeleteSuccess,
	diagramSoftDeleteFailure,

	diagramDeleteSuccess,
	diagramDeleteFailure,

	diagramRestoreSuccess,
	diagramRestoreFailure,

	diagramPinToggleSuccess,
	diagramPinToggleFailure,

	templateCloneSuccess,
	templateCloneFailure,

	invitePeopleSuccess,
	invitePeopleFailure,

	sendFeedbackSuccess,
	sendFeedbackFailure,

	enablePricingPlanLimit,
} from "./main.actions";


import { getDiagramDoc } from '../../lib/yjs';
import { setTitle, setDescription } from '../../lib/yjs/mutators/meta';
import { resolvePlanErrorI18nKey } from '../../lib/plan-errors';

import { showNotify } from "../notify/notify.actions";
import { updateSessionStatus } from '../auth/auth.actions';
import { workspaceFetchInvitesRequest } from '../workspace/workspace.actions';

export function* fetchDiagramsListRequest({ payload: { workspaceId } = {} } = {}) {
	if (!workspaceId) {
		// No workspace selected (mid-bootstrap, zero-workspace user, or sign-out race).
		return;
	}
	try {
		const url = `${requests.workspaces}/${workspaceId}/diagrams`;
		const { data, error } = yield axios.get(url);
		if (error) {
			yield put(fetchDiagramsListFailure(error));
		} else {
			yield put(fetchDiagramsListSuccess(data));
		}
	} catch (e) {
		yield put(fetchDiagramsListFailure(e));
	}
}

// Refetch the diagrams list for whichever workspace is active right now.
// Called from post-CRUD sagas; reading from store (rather than capturing the
// CRUD's payload workspace) means a workspace-switch mid-CRUD lands on the
// new active workspace, not a stale one.
function* refetchActiveWorkspaceDiagrams() {
	const workspaceId = yield select((s) => s.workspace.currentWorkspace?.id);
	yield put(fetchDiagramsList(workspaceId));
}

export function* fetchTemplatesListRequest() {
	try {
		const { data, error } = yield axios.get(requests.templates, {});
		if (error) {
			yield put(fetchTemplatesListFailure(error));
		} else {
			yield put(fetchTemplatesListSuccess(data));
		}
	} catch (e) {
		yield put(fetchTemplatesListFailure(e));
	}
}

export function* diagramRenameRequest({ payload: { id, body } }) {
	try {
		// yield put(showNotify({ message: 'saving' }));
		const url = `${requests.diagrams}/${id}`;
		const { data, error } = yield axios.patch(url, body);
		if (error) {
			yield put(diagramRenameFailure(error));
			yield put(showNotify({ message: 'something_wrong' }));
		} else {
			yield put(diagramRenameSuccess(id, data));
			const doc = getDiagramDoc();
			if (doc) {
				if (data.title) setTitle(doc, data.title);
				if (data.description) setDescription(doc, data.description);
			}
			// Editing the description is an "update"; touching only the title is a
			// "rename". Decide from the request body (the response echoes both fields).
			const descriptionChanged = Object.prototype.hasOwnProperty.call(body || {}, 'description');
			yield put(showNotify({ message: descriptionChanged ? 'diagram_updated' : 'diagram_renamed' }));
		}
	} catch (e) {
		yield put(diagramRenameFailure(e));
		yield put(showNotify({ message: 'something_wrong' }));
	}
}

export function* diagramCreateRequest({ payload: { workspaceId, title, dbEngine, onSuccess } }) {
	try {
		// yield put(showNotify({ message: 'creating' }));
		const url = requests.diagrams;
		const { data, error } = yield axios.post(url, { workspaceId, title, dbEngine });
		if (error) throw error;
		yield put(diagramCreateSuccess(data));
		// yield put(showNotify({ message: 'diagram_created' }));
		yield call(refetchActiveWorkspaceDiagrams);
		if (onSuccess) onSuccess(data);
	} catch (e) {
		const status = e?.response?.status;
		const body = e?.response?.data;

		if (status === 402) {
			if (body?.message === 'plan_limit_reached') {
				const { current, max } = body?.data || {};
				yield put(enablePricingPlanLimit({ type: 'diagrams', current, max }));
			} else {
				yield put(showNotify({ message: resolvePlanErrorI18nKey(body?.message) }));
			}
		} else if (status === 403) {
			yield put(showNotify({ message: 'forbidden' }));
		} else {
			yield put(showNotify({ message: 'something_wrong' }));
		}
		yield put(diagramCreateFailure(e));
	}
}

export function* diagramCloneRequest({ payload: { id, name } }) {
	try {
		// yield put(showNotify({ message: 'duplicating' }));
		const url = `${requests.diagrams}/${id}/clone`;
		const { data, error } = yield axios.post(url, { title: name });
		if (error) throw error;
		yield put(diagramCloneSuccess(id, name));
		yield put(showNotify({ message: 'diagram_duplicated' }));
		yield call(refetchActiveWorkspaceDiagrams);
	} catch (e) {
		const status = e?.response?.status;
		const body = e?.response?.data;

		if (status === 402) {
			if (body?.message === 'plan_limit_reached') {
				const { current, max } = body?.data || {};
				yield put(enablePricingPlanLimit({ type: 'diagrams', current, max }));
			} else {
				yield put(showNotify({ message: resolvePlanErrorI18nKey(body?.message) }));
			}
		} else if (status === 404) {
			yield put(showNotify({ message: 'diagram_not_found' }));
		} else if (status === 403) {
			yield put(showNotify({ message: 'forbidden' }));
		} else {
			yield put(showNotify({ message: 'something_wrong' }));
		}
		yield put(diagramCloneFailure(e));
	}
}

export function* diagramSoftDeleteRequest({ payload: { id, name } }) {
	try {
		// yield put(showNotify({ message: 'deleting' }));
		const url = `${requests.diagrams}/${id}/archive`;
		const { data, error } = yield axios.post(url, { name });
		if (error) {
			yield put(diagramSoftDeleteFailure(error));
			yield put(showNotify({ message: 'something_wrong' }));
		} else {
			yield put(diagramSoftDeleteSuccess(id, name));
			yield put(showNotify({ message: 'diagram_archived' }));
			yield call(refetchActiveWorkspaceDiagrams);
		}
	} catch (e) {
		yield put(diagramSoftDeleteFailure(e));

		const status = e?.response?.status;
		const body = e?.response?.data;
		// Backend returns 409 has_diagram_members when the diagram still has active
		// members and cannot be archived. The error string may arrive as `code` or
		// `message` depending on the endpoint envelope — accept either.
		const errCode = body?.code || body?.message;

		if (status === 409 && errCode === 'has_diagram_members') {
			yield put(showNotify({ message: 'has_diagram_members' }));
		} else {
			yield put(showNotify({ message: 'something_wrong' }));
		}
	}
}

export function* diagramDeleteRequest({ payload: { id, name } }) {
	try {
		// yield put(showNotify({ message: 'deleting' }));
		const url = `${requests.diagrams}/${id}`;
		const { data, error } = yield axios.delete(url, { name });
		if (error) {
			yield put(diagramDeleteFailure(error));
			yield put(showNotify({ message: 'something_wrong' }));
		} else {
			yield put(diagramDeleteSuccess(id, name));
			yield put(showNotify({ message: 'diagram_deleted' }));
			yield call(refetchActiveWorkspaceDiagrams);
		}
	} catch (e) {
		yield put(diagramDeleteFailure(e));
		yield put(showNotify({ message: 'something_wrong' }));
	}
}

export function* diagramRestoreRequest({ payload: { id, name } }) {
	try {
		// yield put(showNotify({ message: 'restoring' }));
		const url = `${requests.diagrams}/${id}/restore`;
		const { data, error } = yield axios.post(url, { name });
		if (error) {
			yield put(diagramRestoreFailure(error));
			yield put(showNotify({ message: 'something_wrong' }));
		} else {
			yield put(diagramRestoreSuccess(id, name));
			yield put(showNotify({ message: 'diagram_restored' }));
			yield call(refetchActiveWorkspaceDiagrams);
		}
	} catch (e) {
		yield put(diagramRestoreFailure(e));
		const status = e?.response?.status;
		const body = e?.response?.data;

		if (status === 402) {
			if (body?.message === 'plan_limit_reached') {
				const { current, max } = body?.data || {};
				yield put(enablePricingPlanLimit({ type: 'diagrams', current, max }));
			} else {
				yield put(showNotify({ message: resolvePlanErrorI18nKey(body?.message) }));
			}
		} else {
			yield put(showNotify({ message: 'something_wrong' }));
		}
	}
}

export function* diagramPinToggleRequest({ payload: { id, nextPinned, prevPinned, prevPinnedAt } }) {
	try {
		// Both endpoints are idempotent (PUT pin / DELETE unpin), so a retry
		// after a network failure is always safe.
		const url = `${requests.diagrams}/${id}/pin`;
		const { error } = nextPinned
			? yield axios.put(url)
			: yield axios.delete(url);
		if (error) throw error;
		yield put(diagramPinToggleSuccess(id, nextPinned));
		yield put(showNotify({ message: nextPinned ? 'diagram_pinned' : 'diagram_unpinned' }));
	} catch (e) {
		yield put(diagramPinToggleFailure({ id, prevPinned, prevPinnedAt, error: e }));
		yield put(showNotify({ message: 'something_wrong' }));
	}
}

export function* templateCloneRequest({ payload: { id, name } }) {
	try {
		// yield put(showNotify({ message: 'creating' }));
		const url = `${requests.templates}/${id}/clone`;
		const { data, error } = yield axios.post(url, { title: name });
		if (error) {
			yield put(templateCloneFailure(error));
			yield put(showNotify({ message: 'something_wrong' }));
		} else {
			yield put(templateCloneSuccess(id, name));
			// yield put(showNotify({ message: 'diagram_created' }));
			yield call(refetchActiveWorkspaceDiagrams);
		}
	} catch (e) {
		yield put(templateCloneFailure(e));
		const status = e?.response?.status;
		const body = e?.response?.data;

		if (status === 402) {
			if (body?.message === 'plan_limit_reached') {
				const { current, max } = body?.data || {};
				yield put(enablePricingPlanLimit({ type: 'diagrams', current, max }));
			} else {
				yield put(showNotify({ message: resolvePlanErrorI18nKey(body?.message) }));
			}
		} else {
			yield put(showNotify({ message: 'something_wrong' }));
		}
	}
}

export function* invitePeopleRequest({ payload: { email } }) {
	const currentWorkspace = yield select((s) => s.workspace?.currentWorkspace);
	if (!currentWorkspace?.id) {
		yield put(invitePeopleFailure(new Error('no_current_workspace')));
		yield put(showNotify({ message: 'something_wrong' }));
		return;
	}
	try {
		const url = `${requests.workspaces}/${currentWorkspace.id}/invites`;
		yield axios.post(url, { email });
		yield put(invitePeopleSuccess(email));
		// Refresh the pending-invites list so the Members page reflects the new
		// invite (owner/admin only; a non-manager's refetch just 403s silently).
		yield put(workspaceFetchInvitesRequest(currentWorkspace.id));
	} catch (e) {
		const status = e?.response?.status;
		const body = e?.response?.data;
		const msg = body?.message;

		if (status === 401) {
			yield put(updateSessionStatus('not_authorized'));
		} else if (status === 400 && msg === 'cannot_invite_self') {
			yield put(showNotify({ message: 'cannot_invite_self' }));
		} else if (status === 400) {
			yield put(showNotify({ message: 'invalid_email' }));
		} else if (status === 403) {
			yield put(showNotify({ message: 'forbidden' }));
		} else if (status === 404) {
			yield put(showNotify({ message: 'workspace_not_found' }));
		} else if (status === 409 && msg === 'invite_already_pending') {
			yield put(showNotify({ message: 'invite_already_pending' }));
		} else if (status === 409 && msg === 'already_member') {
			yield put(showNotify({ message: 'already_member' }));
		} else {
			yield put(showNotify({ message: 'something_wrong' }));
		}
		yield put(invitePeopleFailure(e));
	}
}

export function* sendFeedbackRequest({ payload: { data: body } }) {
	try {
		// yield put(showNotify({ message: 'sending' }));
		const url = `${requests.feedback}`;
		const { data, error } = yield axios.post(url, body);
		if (error) {
			yield put(sendFeedbackFailure(error));
			yield put(showNotify({ message: 'something_wrong' }));
		} else {
			yield put(sendFeedbackSuccess());
			// yield put(showNotify({ message: 'request_sent' }));
		}
	} catch (e) {
		yield put(sendFeedbackFailure(e));
		yield put(showNotify({ message: 'something_wrong' }));
	}
}

export function* onFetchDiagramsList() {
	yield takeLatest(mainActionTypes.MAIN_FETCH_DIAGRAMS_LIST_REQUEST, fetchDiagramsListRequest);
}
export function* onFetchTemplatesList() {
	yield takeLatest(mainActionTypes.MAIN_FETCH_TEMPLATES_LIST_REQUEST, fetchTemplatesListRequest);
}
export function* onDiagramRename() {
	yield takeLatest(mainActionTypes.MAIN_DIAGRAM_RENAME_REQUEST, diagramRenameRequest);
}
export function* onDiagramCreate() {
	yield takeLatest(mainActionTypes.DIAGRAM_CREATE_REQUEST, diagramCreateRequest);
}
export function* onDiagramClone() {
	yield takeLatest(mainActionTypes.MAIN_DIAGRAM_CLONE_REQUEST, diagramCloneRequest);
}
export function* onDiagramSoftDelete() {
	yield takeLatest(mainActionTypes.MAIN_DIAGRAM_SOFT_DELETE_REQUEST, diagramSoftDeleteRequest);
}
export function* onDiagramDelete() {
	yield takeLatest(mainActionTypes.MAIN_DIAGRAM_DELETE_REQUEST, diagramDeleteRequest);
}
export function* onDiagramRestore() {
	yield takeLatest(mainActionTypes.MAIN_DIAGRAM_RESTORE_REQUEST, diagramRestoreRequest);
}
export function* onDiagramStarToggle() {
	yield takeLatest(mainActionTypes.MAIN_DIAGRAM_PIN_TOGGLE_REQUEST, diagramPinToggleRequest);
}
export function* onTemplateClone() {
	yield takeLatest(mainActionTypes.MAIN_TEMPLATE_CLONE_REQUEST, templateCloneRequest);
}
export function* onInvitePeople() {
	yield takeLatest(mainActionTypes.INVITE_PEOPLE_REQUEST, invitePeopleRequest);
}
export function* onSendFeedback() {
	yield takeLatest(mainActionTypes.SUPPORT_REQ_REQUEST, sendFeedbackRequest);
}

export function* mainSagas() {
	yield all
		([
			call(onFetchDiagramsList),
			call(onFetchTemplatesList),
			call(onDiagramRename),
			call(onDiagramCreate),
			call(onDiagramClone),
			call(onDiagramSoftDelete),
			call(onDiagramDelete),
			call(onDiagramRestore),
			call(onDiagramStarToggle),
			call(onTemplateClone),

			call(onInvitePeople),
			call(onSendFeedback),
		]);
}